import { Component, EventEmitter, Output, signal } from '@angular/core';
import { JourneyReports } from '../../../../../services/journey-reports';
import {
	Client,
	JourneySchoolReportData,
	JourneySchoolReportFilter,
	JourneySchoolSectionData,
} from '@mira-avant/mtlibs';
import { JourneySchoolReportSummary } from './journey-school-report-summary/journey-school-report-summary';
import { Column, ReportingTable } from '../../reporting-table/reporting-table';
import { HeaderColumn } from '../../reporting-table/header-column';
import { RowColumn } from '../../reporting-table/row-column';
import { ClientService } from '../../../../../services/client.service';
import { CommonModule } from '@angular/common';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NavigationService } from '../../../../../services/navigation.service';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { DisplayNamePipe } from '../../../../../pipes/display-name.pipe';

@Component({
	selector: 'app-journey-school-report',
	imports: [
		CommonModule,
		JourneySchoolReportSummary,
		ReportingTable,
		RowColumn,
		HeaderColumn,
		NzIconModule,
		DisplayNamePipe,
	],
	standalone: true,
	templateUrl: './journey-school-report.html',
	styleUrl: './journey-school-report.scss',
})
export class JourneySchoolReport {
	@Output('onSelectSection') public onSelectSection = new EventEmitter<string>();

	public report = signal<JourneySchoolReportData | undefined>(undefined);
	public loading = signal<boolean>(true);
	public rows = signal<JourneySchoolSectionData[]>([]);
	public columns = signal<Column[]>([]);
	public client = signal<Client | undefined>(undefined);

	constructor(
		private journeyReportsService: JourneyReports,
		private clientService: ClientService,
		private nav: NavigationService
	) {}

	ngOnInit() {
		this.reload();

		this.clientService.getClientBySubDomain$().then(client => {
			this.client.set(client);
		});
	}

	public async reload(): Promise<void> {
		const matrixParams = await firstValueFrom(this.nav.matrixParams$);
		const locationUuid = matrixParams['locationUuid'];

		await this.updateReport({
			locationUuid: locationUuid,
		});
	}

	public async updateReport(filter: JourneySchoolReportFilter): Promise<void> {
		this.loading.set(true);

		try {
			const params = await firstValueFrom(this.nav.matrixParams$);

			if (!params['product']) {
				throw new Error('Product parameter is missing');
			}

			const reportData = await this.journeyReportsService.getJourneySchoolReport$(params['product'], filter);
			this.report.set(reportData);
			this.rows.set(reportData.sections);

			this.updateTableDetails();
		} catch (error) {
			console.error('Error fetching school report:', error);
		} finally {
			this.loading.set(false);
		}
	}

	private updateTableDetails(): void {
		this.columns.set([
			{
				key: 'sectionName',
				name: 'Class Name',
				sortOrder: 'ascend',
				sortFn: (a: JourneySchoolSectionData, b: JourneySchoolSectionData) =>
					a.sectionName.localeCompare(b.sectionName),
				sortDirections: ['ascend', 'descend', null],
				filterMultiple: true,
				filterOptions: this.rows().map(row => ({ text: row.sectionName, value: row.sectionName })),
				filterFn: (list: string[], item: JourneySchoolSectionData) =>
					list.some(name => item.sectionName.indexOf(name) !== -1),
			},
			{
				key: 'teacher',
				name: 'Teacher',
			},
			{
				key: 'activities',
				name: 'Activities',
				sortOrder: null,
				sortFn: (a: JourneySchoolSectionData, b: JourneySchoolSectionData) =>
					a.activityCount.toString().localeCompare(b.activityCount.toString()),
				sortDirections: ['ascend', 'descend', null],
			},
		]);
	}

	public async onRowClick(row: JourneySchoolSectionData) {
		this.nav.navigateWithMatrix$(['/', 'my', 'reports'], { sectionUuid: row.sectionUuid });
	}
}
